import { DialogCloseEvent, setOverlayClipping } from '@fc/dialog';

/**
 * Predictive Search — autocomplete dropdown for the search-chat input.
 * Fetches section-rendered markup from Shopify's predictive search endpoint so
 * JS only owns interaction, accessibility state, and orchestration.
 */

class PredictiveSearch extends HTMLElement {
  #fetchController = null;
  #eventsController = null;
  #timer = null;
  #activeIndex = -1;
  #renderedQuery = '';
  #statusFrame = null;

  static MIN_CHARS = 2;
  static DEBOUNCE_MS = 300;

  connectedCallback() {
    this.input = this.querySelector('[data-ref="searchInput"], [data-predictive-search-input]');
    this.dropdown = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    this.form = this.querySelector('form');

    if (!this.input || !this.dropdown || !this.form) return;

    this.#applyClosedState();

    this.#eventsController = new AbortController();
    const { signal } = this.#eventsController;

    this.input.addEventListener('input', this.#onInput, { signal });
    this.input.addEventListener('keydown', this.#onKeydown, { signal });
    this.input.addEventListener('focus', this.#onFocus, { signal });
    document.addEventListener('click', this.#onOutsideClick, { signal });

    const overlayDialog = this.closest('overlay-dialog');
    if (overlayDialog) {
      overlayDialog.addEventListener(DialogCloseEvent.eventName, this.#onParentDialogClose, { signal });
    }
  }

  disconnectedCallback() {
    this.#abortFetch();
    clearTimeout(this.#timer);
    this.#eventsController?.abort();
    this.#eventsController = null;
    this.#renderedQuery = '';
    this.#activeIndex = -1;
    this.#cancelStatusFrame();
  }

  close() {
    this.#close();
  }

  #onInput = () => {
    clearTimeout(this.#timer);

    const query = this.#query();
    if (query.length < PredictiveSearch.MIN_CHARS) {
      this.#setBusy(false);
      this.#announceStatus('');
      this.#close();
      return;
    }

    if (query === this.#renderedQuery && this.#hasOptions()) {
      this.#activeIndex = -1;
      this.#syncHighlightedOption();
      this.#open();
      this.#announceResultCount(query);
      return;
    }

    this.#setBusy(true);
    this.#announceStatus(this.#statusMessage('loading', { query }));
    this.#timer = window.setTimeout(() => this.#fetch(query), PredictiveSearch.DEBOUNCE_MS);
  };

  #onFocus = () => {
    const query = this.#query();
    if (query.length >= PredictiveSearch.MIN_CHARS && query === this.#renderedQuery && this.#hasOptions()) {
      this.#open();
    }
  };

  #onOutsideClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (!this.contains(event.target)) this.#close();
  };

  #onParentDialogClose = () => {
    this.#close();
  };

  #onKeydown = (event) => {
    if (!this.#isOpen()) return;

    const options = this.#options();
    if (!options.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#activeIndex = Math.min(this.#activeIndex + 1, options.length - 1);
        this.#syncHighlightedOption();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#activeIndex = Math.max(this.#activeIndex - 1, 0);
        this.#syncHighlightedOption();
        break;
      case 'Enter':
        if (this.#activeIndex >= 0 && options[this.#activeIndex]) {
          event.preventDefault();
          const activeOption = options[this.#activeIndex];
          const link = activeOption.matches('a[href]') ? activeOption : activeOption.querySelector('a[href]');
          if (link instanceof HTMLAnchorElement) window.location.href = link.href;
        }
        this.#close();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.#close();
        this.input.focus();
        break;
      case 'Tab':
        this.#close();
        break;
    }
  };

  #query() {
    return this.input?.value?.trim?.() || '';
  }

  #options() {
    return Array.from(this.dropdown?.querySelectorAll?.('[data-predictive-option]') || []);
  }

  #hasOptions() {
    return this.#options().length > 0;
  }

  #isOpen() {
    return this.dropdown?.hasAttribute('open') ?? false;
  }

  #sectionId() {
    return this.dataset.predictiveSectionId || 'predictive-search';
  }

  #resultLimit() {
    const limit = Number.parseInt(this.dataset.predictiveLimit || '', 10);
    return Number.isFinite(limit) && limit > 0 ? limit : 4;
  }

  #fieldFilter() {
    return this.dataset.predictiveFields?.trim?.() || '';
  }

  #typeFilter() {
    const predictiveTypes = this.dataset.predictiveTypes?.trim?.();
    if (predictiveTypes) return predictiveTypes;

    const input = this.form?.querySelector('input[name="type"]');
    return input instanceof HTMLInputElement && input.value.trim() ? input.value.trim() : 'product';
  }

  #predictiveUrl() {
    if (FitCheck?.routes?.predictive_search_url) return FitCheck.routes.predictive_search_url;

    const root = FitCheck?.routes?.root_url || window.Shopify?.routes?.root;
    if (typeof root === 'string' && root.trim()) return root.replace(/\/?$/, '/') + 'search/suggest';

    return '/search/suggest';
  }

  #abortFetch() {
    if (this.#fetchController) {
      this.#fetchController.abort();
      this.#fetchController = null;
    }
  }

  async #fetch(query) {
    this.#abortFetch();

    const controller = new AbortController();
    this.#fetchController = controller;

    const url = new URL(this.#predictiveUrl(), location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', this.#sectionId());
    url.searchParams.set('resources[type]', this.#typeFilter());
    url.searchParams.set('resources[limit]', String(this.#resultLimit()));

    const fieldFilter = this.#fieldFilter();
    if (fieldFilter) {
      url.searchParams.set('resources[options][fields]', fieldFilter);
    }

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const markup = await response.text();
      if (controller.signal.aborted || this.#query() !== query) return;

      this.#render(markup, query);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.dropdown.replaceChildren();
      this.#announceStatus(this.#statusMessage('error', { query }));
      this.#close();
    } finally {
      if (this.#fetchController === controller) {
        this.#fetchController = null;
      }
      this.#setBusy(false);
    }
  }

  #render(markup, query) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const fragment = doc.querySelector('[data-predictive-search-fragment]');

    if (!(fragment instanceof HTMLElement) || fragment.dataset.hasResults !== 'true') {
      this.dropdown.replaceChildren();
      this.#renderedQuery = query;
      this.#announceStatus(this.#statusMessage('noResults', { query, count: 0 }));
      this.#close();
      return;
    }

    const groups = Array.from(fragment.children).map((child) => document.adoptNode(child));
    this.dropdown.replaceChildren(...groups);

    this.#renderedQuery = query;
    this.#activeIndex = -1;
    this.#assignOptionIds();
    this.#syncHighlightedOption();
    this.#open();
    this.#announceResultCount(query);
  }

  #assignOptionIds() {
    const baseId = this.dropdown.id || 'predictive-results';
    this.#options().forEach((option, index) => {
      option.id = `${baseId}-option-${index}`;
    });
  }

  #syncHighlightedOption() {
    const options = this.#options();

    options.forEach((option, index) => {
      const isActive = index === this.#activeIndex;
      option.setAttribute('aria-selected', String(isActive));
      option.classList.toggle('predictive-search__option--active', isActive);
    });

    if (this.#activeIndex >= 0 && options[this.#activeIndex]) {
      const activeOption = options[this.#activeIndex];
      this.input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  #open() {
    if (!this.#hasOptions()) return;

    setOverlayClipping(this.#clippingElements(), true);

    const searchChat = this.closest('search-chat');
    searchChat?.setAttribute('data-predictive-active', 'true');

    this.dropdown.hidden = false;
    this.dropdown.setAttribute('open', '');
    this.dropdown.removeAttribute('inert');
    this.dropdown.setAttribute('aria-hidden', 'false');
    this.input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    setOverlayClipping(this.#clippingElements(), false);
    this.#applyClosedState();
  }

  #applyClosedState() {
    const searchChat = this.closest('search-chat');
    searchChat?.removeAttribute('data-predictive-active');

    this.dropdown?.removeAttribute('open');
    if (this.dropdown) {
      this.dropdown.hidden = true;
      this.dropdown.setAttribute('inert', '');
      this.dropdown.setAttribute('aria-hidden', 'true');
    }
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
    this.#activeIndex = -1;
    this.#syncHighlightedOption();
  }

  #setBusy(isBusy) {
    if (!this.dropdown) return;
    this.dropdown.setAttribute('aria-busy', String(Boolean(isBusy)));
  }

  #announceResultCount(query) {
    this.#announceStatus(
      this.#statusMessage('results', {
        count: this.#options().length,
        query,
      }),
    );
  }

  #statusMessage(type, values = {}) {
    const templates = {
      loading: this.dataset.predictiveLoadingText || '',
      results: this.dataset.predictiveResultsText || '',
      noResults: this.dataset.predictiveNoResultsText || '',
      error: this.dataset.predictiveErrorText || '',
    };

    return this.#formatStatusMessage(templates[type] || '', values);
  }

  #formatStatusMessage(template, values) {
    return String(template)
      .replaceAll('[count]', String(values.count ?? ''))
      .replaceAll('[query]', String(values.query ?? this.#query()));
  }

  #announceStatus(message) {
    if (!(this.status instanceof HTMLElement)) return;

    const nextMessage = message?.trim();
    this.#cancelStatusFrame();

    if (!nextMessage) {
      this.status.textContent = '';
      return;
    }

    if (this.status.textContent.trim() !== nextMessage) {
      this.status.textContent = nextMessage;
      return;
    }

    this.status.textContent = '';

    this.#statusFrame = requestAnimationFrame(() => {
      this.#statusFrame = null;
      if (this.isConnected && this.status instanceof HTMLElement) {
        this.status.textContent = nextMessage;
      }
    });
  }

  #cancelStatusFrame() {
    if (this.#statusFrame == null) return;
    cancelAnimationFrame(this.#statusFrame);
    this.#statusFrame = null;
  }

  #clippingElements() {
    const container = this.closest('.search-chat__container');
    const dialog = this.closest('dialog');
    const chat = this.closest('search-chat');
    const wrapper = chat?.querySelector?.(':scope > div:last-of-type') || null;
    const grid = this.closest('.fit-search-grid');
    const gridHeader = this.closest('.fit-search-grid__header');
    const blankStack = this.closest('.fit-search-grid__blank-stack');

    return [container, wrapper, dialog, grid, gridHeader, blankStack].filter(
      (element) => element instanceof HTMLElement,
    );
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
